import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { openInNewTab } from '../../utils/openNewTab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolderOpen,
  faSearch,
  faPlus,
  faPlay,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faFileCsv,
  faTimes,
  faSpinner,
  faExternalLinkAlt,
  faAddressBook,
  faInbox,
  faArrowUp,
} from '@fortawesome/free-solid-svg-icons';

import {
  useCaseTypes,
  useStages,
  useFieldDefs,
  useFieldOptions,
  useFilterCases,
  useExportCases,
} from '../../hooks/useCases';
import { useSolicitors } from '../../hooks/useCaseFile';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import {
  OPERATORS,
  getFieldCategory,
  type CaseType,
  type FieldDef,
  type FilterRule,
  type FilterPayload,
} from '../../types/cases';

import DatePicker from '../../components/ui/DatePicker/DatePicker';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import s from './CasesPage.module.css';

/* ── Core columns always requested ── */
const CORE_COLS = ['CaseRef', 'pipeline', 'stage', 'client_name', 'ContactID'];

/* ── Pretty column labels ── */
const COL_LABELS: Record<string, string> = {
  CaseRef: 'Case Ref',
  pipeline: 'Case Type',
  stage: 'Stage',
  client_name: 'Client Name',
  ContactID: 'Contact ID',
  created_at: 'Created',
  updated_at: 'Updated',
};

const isDateLikeFieldType = (fieldType: string | undefined) =>
  fieldType === 'date' ||
  fieldType === 'datetime' ||
  fieldType === 'timestamp' ||
  fieldType === 'timestamptz';

/* ── Helper: format cell values for display ── */
function formatCell(_key: string, val: unknown): string {
  if (val == null || val === '') return '—';
  const str = String(val);
  // ISO-style dates
  if (/^\d{4}-\d{2}-\d{2}(T|\s)/.test(str)) {
    try {
      return new Date(str).toLocaleDateString('en-GB');
    } catch {
      return str;
    }
  }
  // Truncate long text
  if (str.length > 80) return str.slice(0, 77) + '…';
  return str;
}

/* ═════════════════════════════════════════════
 * FieldValueInput — renders the value control
 * for an extra filter row (text / date / select)
 * ═════════════════════════════════════════════ */
function FieldValueInput({
  fieldDef,
  operator,
  value,
  onChange,
}: {
  fieldDef: FieldDef | undefined;
  operator: string;
  value: string | string[];
  onChange: (v: string | string[]) => void;
}) {
  const cat = getFieldCategory(fieldDef);
  const isSolicitorField =
    fieldDef?.key === 'assigned_solicitor' || fieldDef?.key === 'assigned_user_id';
  const needsDropdown = cat === 'dropdown' || cat === 'boolean';
  const { data: fieldOptions = [] } = useFieldOptions(
    needsDropdown && !isSolicitorField && fieldDef ? fieldDef.id : null,
  );
  const { data: solicitors = [] } = useSolicitors();

  // State for multi-select dropdown (Select2 style)
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Build resolved options: for solicitor fields, prefer the solicitors hook
  const options = isSolicitorField
    ? solicitors.map((sol) => ({
        value: sol.username,
        label: sol.displayName || sol.display_name || sol.username,
      }))
    : fieldOptions;

  // Click outside handler for multi-select dropdown
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Empty/notempty operators need no value
  if (['empty', 'notempty'].includes(operator)) return null;

  if (cat === 'boolean') {
    const stringValue = Array.isArray(value) ? value[0] || '' : value;
    return (
      <select value={stringValue} onChange={(e) => onChange(e.target.value)}>
        <option value="">— select —</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (needsDropdown && options.length > 0) {
    // Multi-select for 'in' operator (Select2 styled)
    if (operator === 'in') {
      const arrayValue = Array.isArray(value) ? value : value ? [value as string] : [];
      const filteredOptions = options.filter((o) =>
        (o.label || o.value).toLowerCase().includes(searchTerm.toLowerCase()),
      );

      const toggleOption = (optValue: string) => {
        const newValue = arrayValue.includes(optValue)
          ? arrayValue.filter((v) => v !== optValue)
          : [...arrayValue, optValue];
        onChange(newValue);
      };

      const toggleAll = () => {
        if (arrayValue.length === options.length) {
          onChange([]);
        } else {
          onChange(options.map((o) => o.value));
        }
      };

      return (
        <div className={s.stageDropdown} ref={dropdownRef}>
          <button
            type="button"
            className={`${s.stageToggle} ${dropdownOpen ? s.stageToggleOpen : ''}`}
            onClick={() => setDropdownOpen((v) => !v)}
          >
            <span>
              {arrayValue.length === 0
                ? '— select —'
                : arrayValue.length === options.length
                  ? 'All selected'
                  : `${arrayValue.length} selected`}
            </span>
            <FontAwesomeIcon icon={faChevronDown} />
          </button>

          {dropdownOpen && (
            <div className={s.stageMenu}>
              <div className={s.stageSearchWrap}>
                <input
                  type="text"
                  className={s.stageSearchInput}
                  placeholder="Search options…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
              <label className={s.stageMenuHeader}>
                <input
                  type="checkbox"
                  checked={arrayValue.length === options.length}
                  onChange={toggleAll}
                />
                Select All
              </label>
              {filteredOptions.map((o) => (
                <label key={o.value} className={s.stageOption}>
                  <input
                    type="checkbox"
                    checked={arrayValue.includes(o.value)}
                    onChange={() => toggleOption(o.value)}
                  />
                  {o.label || o.value}
                </label>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Single-select for other operators
    return (
      <select value={Array.isArray(value) ? '' : value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— select —</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label || o.value}
          </option>
        ))}
      </select>
    );
  }

  if (needsDropdown) {
    return (
      <select
        value={Array.isArray(value) ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        disabled
      >
        <option value="">Loading…</option>
      </select>
    );
  }

  if (cat === 'date') {
    const stringValue = Array.isArray(value) ? value[0] || '' : value;
    return <DatePicker value={stringValue} onChange={onChange} />;
  }

  if (cat === 'number') {
    const stringValue = Array.isArray(value) ? value.join(',') : value;
    return (
      <input
        type="number"
        placeholder="Value"
        value={stringValue}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const stringValue = Array.isArray(value) ? value.join(',') : value;
  return (
    <input
      type="text"
      placeholder="Value"
      value={stringValue}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/* ═════════════════════════════════════════════
 * CasesPage — main page component
 * ═════════════════════════════════════════════ */
export default function CasesPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  /* ── data hooks ── */
  const { isSuperAdmin: isSuper, hasPermission, allowedCaseTypes: permissions } = usePermissions();
  const canExportData = isSuper || hasPermission('export_data');
  const { data: allCaseTypes = [] } = useCaseTypes();
  const filterMutation = useFilterCases();
  const { exportCases, exporting, progress: exportProgress } = useExportCases();

  /* ── derived: allowed case types ── */
  const caseTypes = useMemo<CaseType[]>(() => {
    if (!permissions) return allCaseTypes; // null = superadmin
    return allCaseTypes.filter((ct) =>
      permissions.some((p) => p.toLowerCase() === (ct.display_name || ct.name).toLowerCase()),
    );
  }, [allCaseTypes, permissions]);

  /* Set of permitted case type IDs — used to grey out non-permitted options */
  const permittedIds = useMemo(() => new Set(caseTypes.map((ct) => ct.id)), [caseTypes]);

  /* ── filter state ── */
  const [selectedCaseType, setSelectedCaseType] = useState<CaseType | null>(null);
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
  const [stageSearch, setStageSearch] = useState('');
  const stageRef = useRef<HTMLDivElement>(null);
  const tableTopRef = useRef<HTMLDivElement>(null);

  /* extra filter rows */
  interface ExtraFilter {
    id: number;
    fieldKey: string;
    operator: string;
    value: string | string[]; // Support arrays for 'in' operator
  }
  const [extraFilters, setExtraFilters] = useState<ExtraFilter[]>([]);
  const nextFilterId = useRef(1);

  /* ── derive hooks params ── */
  const { data: stages = [] } = useStages(selectedCaseType?.name ?? null);
  const { data: fieldDefs = [] } = useFieldDefs(selectedCaseType?.id ?? null);
  const exportDateColumns = useMemo(
    () =>
      Array.from(
        new Set([
          'created_at',
          'updated_at',
          ...fieldDefs
            .filter((fieldDef) => isDateLikeFieldType(fieldDef.field_type))
            .map((fieldDef) => fieldDef.key),
        ]),
      ),
    [fieldDefs],
  );

  /* ── table state ── */
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [columns, setColumns] = useState<string[]>([]);

  /* ── context menu ── */
  const [ctxMenu, setCtxMenu] = useState<{
    x: number;
    y: number;
    row: Record<string, unknown>;
  } | null>(null);

  /* ── auto-select first case type when loaded ── */
  useEffect(() => {
    if (caseTypes.length > 0 && !selectedCaseType) {
      setSelectedCaseType(caseTypes[0]);
    }
  }, [caseTypes, selectedCaseType]);

  /* ── reset stages + extra filters when case type changes ── */
  useEffect(() => {
    setSelectedStages([]);
    setExtraFilters([]);
    setRows([]);
    setTotalRows(0);
  }, [selectedCaseType?.id]);

  /* ── close stage dropdown on outside click ── */
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (stageRef.current && !stageRef.current.contains(e.target as Node)) {
        setStageDropdownOpen(false);
        setStageSearch('');
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  /* ── close context menu on outside click ── */
  const ctxMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ctxMenu) return;
    const handler = (e: MouseEvent) => {
      if (ctxMenuRef.current?.contains(e.target as Node)) return;
      setCtxMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ctxMenu]);

  /* ─── Build filter payload ─── */
  const buildPayload = useCallback(
    (page: number, limit: number, search?: string): FilterPayload => {
      const rules: FilterRule[] = [];

      // Case type rule — backend maps pipeline → ctyp.display_name
      if (selectedCaseType) {
        rules.push({
          field: 'pipeline',
          operator: 'eq',
          value: selectedCaseType.display_name || selectedCaseType.name,
        });
      }

      // Stage rule (multi-select → IN operator)
      if (selectedStages.length > 0) {
        rules.push({ field: 'stage', operator: 'in', value: selectedStages });
      }

      // Extra filter rows
      for (const ef of extraFilters) {
        if (!ef.fieldKey || !ef.operator) continue;
        // empty / notempty don't need a value
        const hasValue = Array.isArray(ef.value) ? ef.value.length > 0 : !!ef.value;
        if (!['empty', 'notempty'].includes(ef.operator) && !hasValue) continue;
        rules.push({
          field: ef.fieldKey,
          operator: ef.operator,
          value: ef.value || undefined,
        });
      }

      // Columns: core + extra field keys
      const allCols = [
        ...CORE_COLS,
        ...fieldDefs.map((f) => f.key).filter((k) => !CORE_COLS.includes(k)),
      ];

      return {
        rules,
        columns: search ? CORE_COLS : allCols,
        offset: (page - 1) * limit,
        limit,
        searchTerm: search || undefined,
      };
    },
    [selectedCaseType, selectedStages, extraFilters, fieldDefs],
  );

  /* ─── Run filter ─── */
  const runFilter = useCallback(
    (page = 1, search?: string) => {
      if (!selectedCaseType) return;
      const payload = buildPayload(page, pageSize, search);
      filterMutation.mutate(payload, {
        onSuccess: (data) => {
          setRows(data.rows);
          setTotalRows(data.total);
          setCurrentPage(page);
          if (data.rows.length > 0) {
            setColumns(Object.keys(data.rows[0]));
          }
        },
      });
    },
    [selectedCaseType, pageSize, buildPayload, filterMutation],
  );

  /* ─── Pagination ─── */
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    runFilter(page, searchTerm);
  };

  /* Page buttons with ellipsis */
  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('ellipsis');
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, currentPage]);

  /* ─── In-table search (debounced) ─── */
  const searchTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const runFilterRef = useRef(runFilter);
  runFilterRef.current = runFilter;

  const handleTableSearch = (term: string) => {
    setSearchTerm(term);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      runFilterRef.current(1, term);
    }, 400);
  };

  /* ─── Stage toggles ─── */
  const toggleStage = (stageName: string) => {
    setSelectedStages((prev) =>
      prev.includes(stageName) ? prev.filter((s) => s !== stageName) : [...prev, stageName],
    );
  };

  const toggleAllStages = () => {
    if (selectedStages.length === stages.length) {
      setSelectedStages([]);
    } else {
      setSelectedStages(stages.map((st) => st.stage_name));
    }
  };

  /* ─── Extra filters ─── */
  const addExtraFilter = () => {
    setExtraFilters((prev) => [
      ...prev,
      { id: nextFilterId.current++, fieldKey: '', operator: '', value: '' },
    ]);
  };

  const updateExtraFilter = (id: number, patch: Partial<ExtraFilter>) => {
    setExtraFilters((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const updated = { ...f, ...patch };
        // Reset operator & value when field changes
        if (patch.fieldKey !== undefined) {
          updated.operator = '';
          updated.value = '';
        }
        // Reset value when operator changes to/from 'in'
        if (patch.operator !== undefined && patch.operator !== f.operator) {
          const isMulti = patch.operator === 'in';
          updated.value = isMulti ? [] : '';
        }
        return updated;
      }),
    );
  };

  const removeExtraFilter = (id: number) => {
    setExtraFilters((prev) => prev.filter((f) => f.id !== id));
  };

  /* ─── Row context menu actions ─── */
  const openCaseHere = (row: Record<string, unknown>) => {
    const ref = row.CaseRef || row.caseref || row.case_ref;
    if (ref) {
      const qs = selectedCaseType?.id ? `?caseTypeId=${selectedCaseType.id}` : '';
      navigate(`/cases/${ref}${qs}`);
    }
    setCtxMenu(null);
  };

  const openCaseNewTab = (row: Record<string, unknown>) => {
    const ref = row.CaseRef || row.caseref || row.case_ref;
    if (ref) {
      const qs = selectedCaseType?.id ? `?caseTypeId=${selectedCaseType.id}` : '';
      openInNewTab(`/cases/${ref}${qs}`);
    }
    setCtxMenu(null);
  };

  const viewContact = (row: Record<string, unknown>) => {
    const id = row.ContactID || row.contactid || row.contact_id;
    if (id) navigate(`/contacts/${id}`);
    setCtxMenu(null);
  };

  /* ─── CSV export ─── */
  const handleExport = () => {
    if (!selectedCaseType) return;
    const payload = buildPayload(1, 500, searchTerm);
    // Always export a full explicit column set, even when search is active.
    // The search path narrows columns to CORE_COLS for table performance.
    // For CSV we want all mapped fields plus any runtime columns already present.
    payload.columns = Array.from(
      new Set([...CORE_COLS, ...fieldDefs.map((f) => f.key), ...columns]),
    );
    exportCases(payload, totalRows, showToast, { dateColumns: exportDateColumns });
  };

  /* ═══════════════════════ RENDER ═══════════════════════ */

  return (
    <div>
      {/* Page header */}
      <div className={s.pageHeader}>
        <h1 className={s.pageTitle}>
          <FontAwesomeIcon icon={faFolderOpen} /> Cases
        </h1>
      </div>

      {/* ── Simple filter bar ── */}
      <div className={s.filterBar}>
        <div className={s.filterRow}>
          {/* Case type */}
          <div className={s.filterGroup}>
            <label>Case Type</label>
            <select
              className={s.filterSelect}
              value={selectedCaseType?.id?.toString() ?? ''}
              onChange={(e) => {
                const ct = allCaseTypes.find((c) => c.id.toString() === e.target.value) ?? null;
                if (ct && permittedIds.has(ct.id)) setSelectedCaseType(ct);
              }}
            >
              <option value="">— select —</option>
              {allCaseTypes.map((ct) => {
                const allowed = permittedIds.has(ct.id);
                return (
                  <option
                    key={ct.id}
                    value={ct.id}
                    disabled={!allowed}
                    style={{ color: allowed ? undefined : 'var(--gray-400)' }}
                  >
                    {ct.display_name || ct.name}
                    {!allowed ? ' (restricted)' : ''}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Stage multi-select */}
          <div className={s.filterGroup}>
            <label>Stage</label>
            <div className={s.stageDropdown} ref={stageRef}>
              <button
                className={`${s.stageToggle} ${stageDropdownOpen ? s.stageToggleOpen : ''}`}
                onClick={() => setStageDropdownOpen((v) => !v)}
                disabled={!selectedCaseType || stages.length === 0}
              >
                <span>
                  {selectedStages.length === 0
                    ? 'All stages'
                    : selectedStages.length === stages.length
                      ? 'All stages'
                      : `${selectedStages.length} selected`}
                </span>
                <FontAwesomeIcon icon={faChevronDown} />
              </button>

              {stageDropdownOpen && stages.length > 0 && (
                <div className={s.stageMenu}>
                  <div className={s.stageSearchWrap}>
                    <input
                      type="text"
                      className={s.stageSearchInput}
                      placeholder="Search stages…"
                      value={stageSearch}
                      onChange={(e) => setStageSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <label className={s.stageMenuHeader}>
                    <input
                      type="checkbox"
                      checked={selectedStages.length === stages.length}
                      onChange={toggleAllStages}
                    />
                    Select All
                  </label>
                  {stages
                    .filter((st) => st.stage_name.toLowerCase().includes(stageSearch.toLowerCase()))
                    .map((st) => (
                      <label key={st.stage_id} className={s.stageOption}>
                        <input
                          type="checkbox"
                          checked={selectedStages.includes(st.stage_name)}
                          onChange={() => toggleStage(st.stage_name)}
                        />
                        {st.stage_name}
                      </label>
                    ))}
                </div>
              )}
            </div>
          </div>

          {/* Add filter button */}
          <button
            className={s.addFilterBtn}
            onClick={addExtraFilter}
            disabled={!selectedCaseType || fieldDefs.length === 0}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Filter
          </button>

          {/* Run button */}
          <button
            className={s.runFilterBtn}
            onClick={() => runFilter(1)}
            disabled={!selectedCaseType || filterMutation.isPending}
          >
            {filterMutation.isPending ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faPlay} />
            )}
            Run Filter
          </button>

          {/* Total count */}
          {totalRows > 0 && (
            <div className={`${s.filterGroup} ${s.totalCount}`}>
              <label>Total Records</label>
              <span className={s.totalCountNumber}>{totalRows.toLocaleString()}</span>
            </div>
          )}
        </div>

        {/* ── Extra filter rows ── */}
        {extraFilters.length > 0 && (
          <div className={s.extraFilters}>
            {extraFilters.map((ef) => {
              const fd = fieldDefs.find((f) => f.key === ef.fieldKey);
              const cat = getFieldCategory(fd);
              const ops = OPERATORS[cat] ?? OPERATORS.text;

              return (
                <div key={ef.id} className={s.extraFilterRow}>
                  {/* Field selector */}
                  <select
                    value={ef.fieldKey}
                    onChange={(e) => updateExtraFilter(ef.id, { fieldKey: e.target.value })}
                  >
                    <option value="">— field —</option>
                    {fieldDefs.map((f) => (
                      <option key={f.key} value={f.key}>
                        {f.name || f.key}
                      </option>
                    ))}
                  </select>

                  {/* Operator selector */}
                  <select
                    value={ef.operator}
                    onChange={(e) => updateExtraFilter(ef.id, { operator: e.target.value })}
                    disabled={!ef.fieldKey}
                  >
                    <option value="">— operator —</option>
                    {ops.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>

                  {/* Value input */}
                  <FieldValueInput
                    fieldDef={fd}
                    operator={ef.operator}
                    value={ef.value}
                    onChange={(v) => updateExtraFilter(ef.id, { value: v })}
                  />

                  {/* Remove button */}
                  <button
                    className={s.removeFilterBtn}
                    onClick={() => removeExtraFilter(ef.id)}
                    title="Remove filter"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Results table ── */}
      <div className={s.results}>
        {/* Toolbar */}
        <div className={s.tableToolbar}>
          <div className={s.toolbarLeft}>
            <span>Show</span>
            <select
              className={s.pageSizeSelect}
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);
                if (rows.length > 0) {
                  // Re-run with new page size from page 1
                  const payload = buildPayload(1, newSize, searchTerm);
                  filterMutation.mutate(payload, {
                    onSuccess: (data) => {
                      setRows(data.rows);
                      setTotalRows(data.total);
                      setCurrentPage(1);
                      if (data.rows.length > 0) setColumns(Object.keys(data.rows[0]));
                    },
                  });
                }
              }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>

          <div className={s.toolbarCenter}>
            <div className={s.tableSearchWrap}>
              <FontAwesomeIcon icon={faSearch} />
              <input
                className={s.tableSearchInput}
                type="text"
                placeholder="Search in results…"
                value={searchTerm}
                onChange={(e) => handleTableSearch(e.target.value)}
              />
            </div>
          </div>

          <button
            className={s.exportBtn}
            disabled={totalRows === 0 || exporting || !canExportData}
            onClick={handleExport}
            title={!canExportData ? 'Permission Blocked' : undefined}
          >
            <FontAwesomeIcon icon={faFileCsv} />
            {exporting ? `Exporting ${exportProgress}%` : 'Export CSV'}
          </button>
        </div>

        {/* Table body */}
        {filterMutation.isPending && rows.length === 0 ? (
          <div className={s.loadingOverlay}>
            <FontAwesomeIcon icon={faSpinner} spin />
            Loading cases…
          </div>
        ) : rows.length === 0 ? (
          <div className={s.emptyResults}>
            <FontAwesomeIcon icon={faInbox} />
            <h3>No cases to display</h3>
            <p>Select a case type and click "Run Filter" to search.</p>
          </div>
        ) : (
          <>
            {/* Top pagination */}
            <div className={s.pagination}>
              <div className={s.paginationInfo}>
                Showing {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, totalRows)} of {totalRows.toLocaleString()}
              </div>
              <div className={s.paginationButtons}>
                <button
                  className={s.pageBtn}
                  disabled={currentPage === 1}
                  onClick={() => goToPage(currentPage - 1)}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                {pageNumbers.map((p, i) =>
                  p === 'ellipsis' ? (
                    <span key={`et${i}`} className={s.ellipsis}>
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={`${s.pageBtn} ${p === currentPage ? s.pageBtnActive : ''}`}
                      onClick={() => goToPage(p as number)}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  className={s.pageBtn}
                  disabled={currentPage === totalPages}
                  onClick={() => goToPage(currentPage + 1)}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>

            <div className={s.tableScroll} ref={tableTopRef}>
              <table className={s.casesTable}>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col}>{COL_LABELS[col] || col.replace(/_/g, ' ')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr
                      key={`${row.CaseRef || idx}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setCtxMenu({ x: e.clientX, y: e.clientY, row });
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setCtxMenu({ x: e.clientX, y: e.clientY, row });
                      }}
                    >
                      {columns.map((col) => (
                        <td key={col}>{formatCell(col, row[col])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={s.pagination}>
              <div className={s.paginationInfo}>
                Showing {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, totalRows)} of {totalRows.toLocaleString()}
              </div>
              <div className={s.paginationButtons}>
                <button
                  className={s.pageBtn}
                  disabled={currentPage === 1}
                  onClick={() => goToPage(currentPage - 1)}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                {pageNumbers.map((p, i) =>
                  p === 'ellipsis' ? (
                    <span key={`e${i}`} className={s.ellipsis}>
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      className={`${s.pageBtn} ${p === currentPage ? s.pageBtnActive : ''}`}
                      onClick={() => goToPage(p as number)}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  className={s.pageBtn}
                  disabled={currentPage === totalPages}
                  onClick={() => goToPage(currentPage + 1)}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
                <button
                  className={s.goToTopBtn}
                  onClick={() => tableTopRef.current?.scrollIntoView({ behavior: 'smooth' })}
                  title="Go to top"
                >
                  <FontAwesomeIcon icon={faArrowUp} /> Top
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Context menu (row actions) ── */}
      {ctxMenu && (
        <div ref={ctxMenuRef} className={s.contextMenu} style={{ left: ctxMenu.x, top: ctxMenu.y }}>
          <button className={s.contextMenuItem} onClick={() => openCaseHere(ctxMenu.row)}>
            <FontAwesomeIcon icon={faFolderOpen} /> Open Case
          </button>
          <button
            className={s.contextMenuIconBtn}
            onClick={() => openCaseNewTab(ctxMenu.row)}
            title="Open in New Tab"
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </button>
          <div className={s.contextMenuDivider} />
          <button className={s.contextMenuItem} onClick={() => viewContact(ctxMenu.row)}>
            <FontAwesomeIcon icon={faAddressBook} /> View Contact
          </button>
          <div className={s.contextMenuDivider} />
          <button
            className={`${s.contextMenuItem} ${s.contextMenuCancel}`}
            onClick={() => setCtxMenu(null)}
          >
            <FontAwesomeIcon icon={faTimes} /> Cancel
          </button>
        </div>
      )}
    </div>
  );
}
